// services/toolsService.ts
import { api } from '@edemics/lib/api'

class ToolsService {
  private getHeaders() {
    if (typeof window !== 'undefined') {
      const token = sessionStorage.getItem('token')
      return token ? { Authorization: `Bearer ${token}` } : {}
    }
    return {}
  }

  private async handleRequest<T>(promise: Promise<any>): Promise<T> {
    const response = await promise
    return response.data
  }

  // ==================== PERMISSION RULES ====================

  async getPermissionRules(filters?: { documentType?: string; role?: string }): Promise<any[]> {
    return this.handleRequest(
      api.get('/tools/permission-rules', { params: filters, headers: this.getHeaders() })
    )
  }

  async getPermissionRule(id: string): Promise<any> {
    return this.handleRequest(
      api.get(`/tools/permission-rules/${id}`, { headers: this.getHeaders() })
    )
  }

  async createPermissionRule(data: { documentType: string; role: string; level: number; creatorRestriction?: string; permissions?: { id: string; name: string; checked: boolean }[] }): Promise<any> {
    return this.handleRequest(
      api.post('/tools/permission-rules', data, { headers: this.getHeaders() })
    )
  }

  async updatePermissionRule(id: string, data: { level?: number; creatorRestriction?: string; permissions?: { id: string; name: string; checked: boolean }[] }): Promise<any> {
    return this.handleRequest(
      api.put(`/tools/permission-rules/${id}`, data, { headers: this.getHeaders() })
    )
  }

  async deletePermissionRule(id: string): Promise<void> {
    return this.handleRequest(
      api.delete(`/tools/permission-rules/${id}`, { headers: this.getHeaders() })
    )
  }

  async getPermissionDocumentTypes(): Promise<string[]> {
    return this.handleRequest(
      api.get('/tools/permission-rules/document-types', { headers: this.getHeaders() })
    )
  }

  async getPermissionRoles(): Promise<string[]> {
    return this.handleRequest(
      api.get('/tools/permission-rules/roles', { headers: this.getHeaders() })
    )
  }

  async getPermissionDefaults(role: string): Promise<{
    role: string
    templates: Array<{
      documentType: string
      level: number
      creatorRestriction: string
      permissions: Array<{ id: string; name: string; checked: boolean }>
      alreadyExists: boolean
    }>
    existingCount: number
    newCount: number
  }> {
    return this.handleRequest(
      api.get(`/tools/permission-rules/defaults/${encodeURIComponent(role)}`, { headers: this.getHeaders() })
    )
  }

  async bulkCreatePermissionRules(data: {
    rules: Array<{ documentType: string; role: string; level: number; creatorRestriction?: string }>
    overwriteExisting?: boolean
  }): Promise<{ created: number; updated: number; skipped: number; errors: string[] }> {
    return this.handleRequest(
      api.post('/tools/permission-rules/bulk', data, { headers: this.getHeaders() })
    )
  }

  // ==================== MY PERMISSIONS (for enforcement context) ====================

  async getMyPermissions(): Promise<any[]> {
    return this.handleRequest(
      api.get('/tools/permission-rules/my-permissions', { headers: this.getHeaders() })
    )
  }

  // ==================== MODULE ASSIGNMENTS ====================

  async getModuleAssignments(role?: string): Promise<any[]> {
    return this.handleRequest(
      api.get('/tools/module-assignments', {
        params: role ? { role } : {},
        headers: this.getHeaders(),
      })
    )
  }

  async getMyModules(): Promise<string[]> {
    return this.handleRequest(
      api.get('/tools/module-assignments/my-modules', { headers: this.getHeaders() })
    )
  }

  async bulkUpsertModuleAssignments(
    assignments: { moduleKey: string; role: string; enabled: boolean }[]
  ): Promise<{ updated: number }> {
    return this.handleRequest(
      api.post('/tools/module-assignments/bulk', { assignments }, { headers: this.getHeaders() })
    )
  }

  async resetModuleAssignments(role: string): Promise<void> {
    return this.handleRequest(
      api.delete(`/tools/module-assignments/reset/${role}`, { headers: this.getHeaders() })
    )
  }

  // ==================== ROLES ====================

  async getRoles(): Promise<{ systemRoles: any[]; customRoles: any[] }> {
    return this.handleRequest(
      api.get('/tools/roles', { headers: this.getHeaders() })
    )
  }

  async createRole(data: { displayName: string; baseRole?: string }): Promise<any> {
    return this.handleRequest(
      api.post('/tools/roles', data, { headers: this.getHeaders() })
    )
  }

  async updateRole(id: string, data: { displayName?: string; baseRole?: string; isActive?: boolean }): Promise<any> {
    return this.handleRequest(
      api.put(`/tools/roles/${id}`, data, { headers: this.getHeaders() })
    )
  }

  async deleteRole(id: string): Promise<{ success: boolean; message: string }> {
    return this.handleRequest(
      api.delete(`/tools/roles/${id}`, { headers: this.getHeaders() })
    )
  }

  async getRoleDefaultPermissions(role: string): Promise<any[]> {
    return this.handleRequest(
      api.get(`/tools/roles/defaults/${encodeURIComponent(role)}`, { headers: this.getHeaders() })
    )
  }
}

export const toolsService = new ToolsService()
